import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Database, Settings, Shield, Calendar, List, Package, Image } from 'lucide-react';
import { AdminPageBackgroundManager } from './AdminPageBackgroundManager';
import { useToast } from '@/hooks/use-toast';
import { AdminSectionManager } from './AdminSectionManager';
import { AdminPhaseManager } from './AdminPhaseManager';
import { AdminItemManager } from './AdminItemManager';
import { supabase } from '@/integrations/supabase/client';

interface Section {
  id: string;
  template_name: string;
  display_order: number;
}

interface Phase {
  id: string;
  section_id: string;
  phase_title: string;
  display_order: number;
}

export const AdminTriathlonPanel = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<Phase | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [activeTab, setActiveTab] = useState('sections');
  const { toast } = useToast();

  const loadSections = async () => {
    try {
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .order('display_order');
      
      if (error) throw error;
      setSections((data as any[]) || []);
      
      // Auto-select first section
      if (data && data.length > 0 && !selectedSection) {
        setSelectedSection(data[0] as any);
      }
      
      // Notify parent to refresh data
      window.dispatchEvent(new CustomEvent('adminDataChanged'));
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  const loadPhases = async (sectionId: string) => {
    try {
      const { data, error } = await supabase
        .from('template_phases')
        .select('*')
        .eq('template_id', sectionId)
        .order('display_order');
      
      if (error) throw error;
      setPhases((data as any[]) || []);
    } catch (error) {
      console.error('Error loading phases:', error);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      loadPhases(selectedSection.id);
    }
  }, [selectedSection]);

  return (
    <Card className="m-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Admin Dashboard - Triathlon Content Management
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="sections" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Sections
            </TabsTrigger>
            <TabsTrigger value="phases" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Phases
            </TabsTrigger>
            <TabsTrigger value="items" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Items
            </TabsTrigger>
            <TabsTrigger value="images" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              Images
            </TabsTrigger>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Overview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sections" className="space-y-4">
            <AdminSectionManager />
          </TabsContent>

          <TabsContent value="phases" className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-light mb-2">Select Section</h3>
              <div className="flex flex-wrap gap-2">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    variant={selectedSection?.id === section.id ? "default" : "outline"}
                    onClick={() => setSelectedSection(section)}
                  >
                    {section.template_name}
                  </Button>
                ))}
              </div>
            </div>
            {selectedSection && (
              <AdminPhaseManager 
                sectionId={selectedSection.id}
                sectionName={selectedSection.template_name}
              />
            )}
          </TabsContent>

          <TabsContent value="items" className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-light mb-2">Select Section</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    variant={selectedSection?.id === section.id ? "default" : "outline"}
                    onClick={() => setSelectedSection(section)}
                  >
                    {section.template_name}
                  </Button>
                ))}
              </div>
              
              {selectedSection && phases.length > 0 && (
                <div>
                  <h4 className="font-normal mb-2">Select Phase in {selectedSection.template_name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {phases.map((phase) => (
                      <Button
                        key={phase.id}
                        variant={selectedPhase?.id === phase.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedPhase(phase)}
                      >
                        {phase.phase_title}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {selectedPhase && (
              <AdminItemManager 
                phaseId={selectedPhase.id}
                phaseTitle={selectedPhase.phase_title}
              />
            )}
          </TabsContent>

          <TabsContent value="images" className="space-y-6">
            <AdminPageBackgroundManager />
          </TabsContent>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Database className="h-8 w-8 text-primary" />
                    <div>
                      <h3 className="font-light">Database Status</h3>
                      <p className="text-sm text-muted-foreground">
                        Admin tables are active and populated
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Badge variant="secondary">Sections: {sections.length}</Badge>
                    <Badge variant="outline">Fully functional admin interface</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Settings className="h-8 w-8 text-primary" />
                    <div>
                      <h3 className="font-light">Admin Features</h3>
                      <p className="text-sm text-muted-foreground">
                        Complete CRUD operations available
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Badge variant="secondary">Create, Edit, Delete content</Badge>
                    <Badge variant="outline">Real-time database updates</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="rounded-lg bg-muted/50 p-4">
              <h4 className="font-light mb-2">Available Admin Functions:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground font-normal">
                <li>• <span className="font-normal">Sections Tab:</span> Manage triathlon sections (rename, set target dates, enable countdowns)</li>
                <li>• <span className="font-normal">Phases Tab:</span> Create and edit phases within sections (Pre-Race, Swim, Bike, Run, etc.)</li>
                <li>• <span className="font-normal">Items Tab:</span> Add/edit checklist items with notes, ads URLs, and status flags</li>
                <li>• <span className="font-normal">Real-time Updates:</span> Changes are immediately saved to the database</li>
                <li>• <span className="font-normal">User Experience:</span> Regular users see your admin-managed content seamlessly</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};